import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input, Select, Button, Table, Tooltip, Divider, Tabs, DatePicker, Image } from 'antd';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from 'react-responsive-carousel';
import { AppstoreOutlined, CalendarOutlined, CarOutlined, ClearOutlined, DownloadOutlined, FilterOutlined, InfoCircleOutlined, MinusOutlined, NumberOutlined, PlusOutlined, SearchOutlined, TableOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import uzdLogo from '../assets/images/uzd_logo_small.jpeg';
import { API_DOMAIN } from '../components/constants';
import useSnackbar from '../components/Snackbar/UseSnackbar';

const { RangePicker } = DatePicker;
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 1979 }, (_, i) => {
    const y = currentYear - i;
    return { value: y, label: String(y) };
});

const labelStyle = { fontSize: '11px', fontWeight: 600, color: '#4b5563', marginBottom: '3px', display: 'block', letterSpacing: '0.03em' };

function FieldLabel({ children }) {
    return <span style={labelStyle}>{children}</span>;
}

function SearchResults() {
    const { ErrorSnackbar, ToastContainer } = useSnackbar();

    const head = { headers: { accessToken: localStorage.getItem('accessToken') } };

    // Search fields
    const [queryId, setQueryId] = useState('');
    const [vin, setVin] = useState('');
    const [vinError, setVinError] = useState(false);
    const [make, setMake] = useState(null);
    const [model, setModel] = useState(null);
    const [year, setYear] = useState(null);
    const [region, setRegion] = useState(null);
    const [part, setPart] = useState(null);
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [dateRange, setDateRange] = useState(null);

    // Dropdown datasets
    const [makeOptions, setMakeOptions] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);
    const [partOptions, setPartOptions] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);

    // Results
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [activeTab, setActiveTab] = useState('search');
    const [galleryColumns, setGalleryColumns] = useState(4);
    const [currentSlides, setCurrentSlides] = useState({});

    // Load dropdowns on mount
    useEffect(() => {
        axios.post(API_DOMAIN + '/whatsapp/get_makes_v2', {}, head)
            .then(res => {
                if (res.data.success)
                    setMakeOptions(res.data.data.map(i => ({ value: i.id, label: i.name })));
            })
            .catch(() => { });

        axios.post(API_DOMAIN + '/whatsapp/get_region', head)
            .then(res => {
                if (res.data.data)
                    setRegionOptions(res.data.data.map(i => ({ value: i.id, label: i.region })));
            })
            .catch(() => { });

        axios.post(API_DOMAIN + '/whatsapp/get_parts_info', head)
            .then(res => {
                if (res.data.success)
                    setPartOptions(res.data.data);
            })
            .catch(() => { });
    }, []);

    const onMakeChange = async (value) => {
        setMake(value);
        setModel(null);
        setModelOptions([]);
        if (!value) return;
        setLoadingModels(true);
        try {
            const res = await axios.post(API_DOMAIN + '/whatsapp/get_models_v2', { make: value });
            if (res.data.success)
                setModelOptions(res.data.data.map(i => ({ value: i.id, label: i.model })));
        } catch {
            ErrorSnackbar('Failed to load models');
        } finally {
            setLoadingModels(false);
        }
    };

    const handleVinChange = (e) => {
        const val = e.target.value.toUpperCase();
        setVin(val);
        setVinError(val.length > 0 && !VIN_REGEX.test(val));
    };

    const handleClearAll = () => {
        setQueryId('');
        setVin('');
        setVinError(false);
        setMake(null);
        setModel(null);
        setModelOptions([]);
        setYear(null);
        setRegion(null);
        setPart(null);
        setAdditionalInfo('');
        setDateRange(null);
        setResults([]);
    };

    const loadImage = (src, crossOrigin) => new Promise((resolve, reject) => {
        const img = new window.Image();
        if (crossOrigin) img.crossOrigin = crossOrigin;
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

    const handleDownload = async (imgPath, filename) => {
        try {
            const [original, watermark] = await Promise.all([
                loadImage(API_DOMAIN + '/' + imgPath, 'anonymous'),
                loadImage(uzdLogo),
            ]);

            const canvas = document.createElement('canvas');
            canvas.width = original.naturalWidth;
            canvas.height = original.naturalHeight;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(original, 0, 0);

            const wmMaxWidth = Math.min(140, canvas.width * 0.22);
            const wmScale = wmMaxWidth / watermark.naturalWidth;
            const wmW = watermark.naturalWidth * wmScale;
            const wmH = watermark.naturalHeight * wmScale;
            const margin = 10;

            ctx.globalAlpha = 0.4;
            ctx.drawImage(watermark, margin, canvas.height - wmH - margin, wmW, wmH);
            ctx.globalAlpha = 1;

            canvas.toBlob(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || imgPath.split('/').pop() || 'image';
                a.click();
                URL.revokeObjectURL(url);
            }, 'image/jpeg', 0.95);
        } catch {
            ErrorSnackbar('Failed to download image');
        }
    };

    const handleSearch = async () => {
        if (vinError) {
            ErrorSnackbar('Invalid VIN — must be 17 alphanumeric characters (no I, O, Q)');
            return;
        }
        setSearching(true);
        try {
            const payload = {
                query_id:      queryId || null,
                date_from:     dateRange?.[0]?.format('YYYY-MM-DD') || null,
                date_to:       dateRange?.[1]?.format('YYYY-MM-DD') || null,
                make:          makeOptions.find(o => o.value === make)?.label || null,
                model:         modelOptions.find(o => o.value === model)?.label || null,
                model_details: additionalInfo || null,
                chassis_no:    vin || null,
                region:        region || null,
                year:          year || null,
                body_part:     partOptions.find(o => o.value === part)?.label || null,
            };
            const res = await axios.post(API_DOMAIN + '/parts_search/parts_main_search', payload, head);
            if (res.data.success) {
                setResults(res.data.data);
                console.log(JSON.stringify(res.data.data));

            } else {
                ErrorSnackbar(res.data.message || 'No results found');
                setResults([]);
            }
        } catch (err) {
            ErrorSnackbar(err.message);
        } finally {
            setSearching(false);
        }
    };

    const columns = [
        { title: 'Query ID',           dataIndex: 'query_id',           key: 'query_id',           width: 100 },
        { title: 'Date',               dataIndex: 'date',               key: 'date',               width: 110 },
        { title: 'Chassis No',         dataIndex: 'chassis_no',         key: 'chassis_no',         width: 180 },
        {
            title: 'Vehicle', key: 'vehicle', width: 220,
            render: (_, row) => {
                const parts = [row.make];
                if (row.model) parts[0] = `${row.make} - ${row.model}`;
                const extras = [row.year, row.region].filter(Boolean).map(v => `(${v})`);
                return [...parts, ...extras].join(' ') || '—';
            },
        },
        { title: 'Part',               dataIndex: 'body_part',          key: 'body_part',          width: 160 },
        { title: 'Best Price',     dataIndex: 'best_price_received', key: 'best_price_received', width: 110 },
        { title: 'Delivery Cost', dataIndex: 'delivery_cost',      key: 'delivery_cost',      width: 120 },
        { title: 'Price Given',   dataIndex: 'price_given',         key: 'price_given',        width: 110 },
    ];

    return (
        <>
            <ToastContainer />

            {/* Search panel */}
            <div
                style={{
                    position: 'sticky',
                    top: 70,
                    zIndex: 1000,
                    background: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                }}
            >
                {/* Panel header */}
                <div
                    className="flex items-center gap-2 px-4 py-2"
                    style={{
                        background: 'linear-gradient(90deg, #1d4ed8 0%, #3b82f6 60%, #64aafa 100%)',
                    }}
                >
                    <FilterOutlined style={{ color: 'white', fontSize: 15 }} />
                    <span style={{ color: 'white', fontWeight: 700, fontSize: 14, letterSpacing: '0.04em' }}>
                        Search Filters
                    </span>
                </div>

                {/* Inputs grid */}
                <div className="px-4 py-3" style={{ background: '#f8fafc' }}>
                    <div className="flex flex-wrap gap-4 items-end">

                        {/* Query ID */}
                        <div>
                            <FieldLabel><NumberOutlined style={{ marginRight: 4 }} />Query ID</FieldLabel>
                            <Input
                                style={{ width: '140px' }}
                                allowClear
                                placeholder="e.g. 10234"
                                value={queryId}
                                onChange={e => setQueryId(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            />
                        </div>

                        {/* VIN */}
                        <div>
                            <FieldLabel><CarOutlined style={{ marginRight: 4 }} />VIN</FieldLabel>
                            <Tooltip title={vinError ? 'Invalid VIN — 17 alphanumeric chars, no I/O/Q' : 'Vehicle Identification Number'}>
                                <Input
                                    style={{ width: '210px' }}
                                    allowClear
                                    placeholder="17-char VIN"
                                    value={vin}
                                    onChange={handleVinChange}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                                    status={vinError ? 'error' : ''}
                                />
                            </Tooltip>
                        </div>

                        {/* Date Range */}
                        <div>
                            <FieldLabel><CalendarOutlined style={{ marginRight: 4 }} />Query Date</FieldLabel>
                            <RangePicker
                                style={{ width: '230px' }}
                                value={dateRange}
                                onChange={setDateRange}
                                format="DD/MM/YYYY"
                                allowClear
                                disabledDate={d => d && d.isAfter(dayjs(), 'day')}
                            />
                        </div>

                        <Divider type="vertical" style={{ height: '48px', margin: '0' }} />

                        {/* Make */}
                        <div>
                            <FieldLabel>Make</FieldLabel>
                            <Select
                                style={{ width: '150px' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select Make"
                                value={make}
                                onChange={onMakeChange}
                                options={makeOptions}
                            />
                        </div>

                        {/* Model */}
                        <div>
                            <FieldLabel>Model</FieldLabel>
                            <Select
                                style={{ width: '150px' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select Model"
                                value={model}
                                onChange={setModel}
                                options={modelOptions}
                                loading={loadingModels}
                                disabled={!make}
                            />
                        </div>

                        {/* Year */}
                        <div>
                            <FieldLabel>Mfg. Year</FieldLabel>
                            <Select
                                style={{ width: '110px' }}
                                allowClear
                                showSearch
                                placeholder="Year"
                                value={year}
                                onChange={setYear}
                                options={yearOptions}
                            />
                        </div>

                        <Divider type="vertical" style={{ height: '48px', margin: '0' }} />

                        {/* Region */}
                        <div>
                            <FieldLabel>Region</FieldLabel>
                            <Select
                                style={{ width: '140px' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select Region"
                                value={region}
                                onChange={setRegion}
                                options={regionOptions}
                            />
                        </div>

                        {/* Part */}
                        <div>
                            <FieldLabel>Part</FieldLabel>
                            <Select
                                style={{ width: '220px' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select Part"
                                value={part}
                                onChange={setPart}
                                options={partOptions}
                            />
                        </div>

                        <Divider type="vertical" style={{ height: '48px', margin: '0' }} />

                        {/* Additional Info */}
                        <div>
                            <FieldLabel><InfoCircleOutlined style={{ marginRight: 4 }} />Additional Info</FieldLabel>
                            <Input
                                style={{ width: '220px' }}
                                allowClear
                                placeholder="Additional model information"
                                value={additionalInfo}
                                onChange={e => setAdditionalInfo(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-auto">
                            <Button
                                icon={<ClearOutlined />}
                                onClick={handleClearAll}
                                style={{ borderColor: '#9ca3af', color: '#6b7280' }}
                            >
                                Clear
                            </Button>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                loading={searching}
                                style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)', border: 'none', fontWeight: 600, paddingInline: '20px' }}
                            >
                                Search
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs: Search Results | Image Gallery */}
            <div className="mt-3">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={[
                        {
                            key: 'search',
                            label: <span><TableOutlined style={{ marginRight: 6 }} />Search Results</span>,
                            children: (
                                <Table
                                    size="small"
                                    dataSource={results}
                                    columns={columns}
                                    rowKey="query_id"
                                    loading={searching}
                                    expandable={{
                                        expandedRowRender: row => (
                                            <div className="p-2 text-sm text-gray-600 flex gap-6">
                                                {row.model_details && (
                                                    <span><strong>Model Details:</strong> {row.model_details}</span>
                                                )}
                                                {row.chassis_no && (
                                                    <span><strong>Chassis No:</strong> {row.chassis_no}</span>
                                                )}
                                                {row.supplier_name && (
                                                    <span><strong>Supplier:</strong> {row.supplier_name}</span>
                                                )}
                                                {row.supplier_number && (
                                                    <span><strong>Supplier No:</strong> {row.supplier_number}</span>
                                                )}
                                            </div>
                                        ),
                                        rowExpandable: row => !!(row.model_details || row.chassis_no || row.supplier_name || row.supplier_number),
                                    }}
                                    locale={{ emptyText: 'Use the filters above and press Search' }}
                                    pagination={{ pageSize: 20, showSizeChanger: true }}
                                />
                            ),
                        },
                        {
                            key: 'gallery',
                            label: <span><AppstoreOutlined style={{ marginRight: 6 }} />Image Gallery</span>,
                            children: results.length === 0 ? (
                                <div style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#9ca3af' }}>
                                    <AppstoreOutlined style={{ fontSize: 48, color: '#d1d5db' }} />
                                    <span style={{ fontSize: 15, fontWeight: 500 }}>No results yet</span>
                                    <span style={{ fontSize: 12 }}>Search for a query to view associated images here.</span>
                                </div>
                            ) : (
                                <>
                                    {/* Zoom controls */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>Columns:</span>
                                        <Button size="small" icon={<MinusOutlined />} onClick={() => setGalleryColumns(c => Math.max(1, c - 1))} disabled={galleryColumns <= 1} />
                                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 16, textAlign: 'center' }}>{galleryColumns}</span>
                                        <Button size="small" icon={<PlusOutlined />} onClick={() => setGalleryColumns(c => Math.min(8, c + 1))} disabled={galleryColumns >= 8} />
                                    </div>

                                    {/* Card grid */}
                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${galleryColumns}, 1fr)`, gap: 12 }}>
                                        {results.map(row => (
                                            <div
                                                key={row.query_id}
                                                style={{ borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', background: 'white', overflow: 'visible' }}
                                            >
                                                {/* Banner */}
                                                <div style={{ background: 'linear-gradient(90deg, #1d4ed8 0%, #3b82f6 100%)', padding: '8px 10px' }}>
                                                    <div style={{ fontWeight: 700, fontSize: 12, color: 'white' }}>#{row.query_id}</div>
                                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', marginTop: 1 }}>
                                                        {[row.make, row.model].filter(Boolean).join(' – ') || '—'}
                                                        {row.year && <span style={{ opacity: 0.75 }}> ({row.year})</span>}
                                                    </div>
                                                    {row.body_part && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>{row.body_part}</div>}
                                                </div>

                                                {/* Carousel */}
                                                {row.image && row.image.length > 0 ? (
                                                    <>
                                                        <Image.PreviewGroup>
                                                        <Carousel
                                                            showArrows
                                                            infiniteLoop
                                                            showThumbs={false}
                                                            showStatus={false}
                                                            swipeable
                                                            emulateTouch
                                                            onChange={idx => setCurrentSlides(prev => ({ ...prev, [row.query_id]: idx }))}
                                                            style={{ border: 'none', outline: 'none' }}
                                                        >
                                                            {row.image.map((img, idx) => {
                                                                const imgPath = typeof img === 'string' ? img : (img.url || img.path);
                                                                return (
                                                                    <div key={idx}>
                                                                        <Image
                                                                            src={API_DOMAIN + '/' + imgPath}
                                                                            alt={`Query ${row.query_id} #${idx + 1}`}
                                                                            style={{ width: '100%', height: 180, objectFit: 'contain', background: '#f8fafc' }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </Carousel>
                                                        </Image.PreviewGroup>
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px 6px', borderTop: '1px solid #f0f0f0' }}>
                                                            <Button
                                                                size="small"
                                                                icon={<DownloadOutlined />}
                                                                onClick={() => {
                                                                    const idx = currentSlides[row.query_id] || 0;
                                                                    const img = row.image[idx];
                                                                    const imgPath = typeof img === 'string' ? img : (img.url || img.path);
                                                                    handleDownload(imgPath, `query_${row.query_id}_${idx + 1}`);
                                                                }}
                                                            >
                                                                Download
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#d1d5db' }}>
                                                        <AppstoreOutlined style={{ fontSize: 32 }} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );
}

export default SearchResults;
